import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Leaf, Trees, Droplets, Trash2, ShieldAlert, Mountain, Globe, 
  ArrowLeft, ArrowRight, Sparkles, ShieldCheck, FileText, CheckCircle2,
  Radio, Radiation, Flame, Waves, SunMedium, Pickaxe, Activity
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import EcoCopernicusSoilUkfAnalyzer from './EcoCopernicusSoilUkfAnalyzer';

interface EcoCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const ECO_AGENT_IDS = [
  'eco-space-strategy-architect',
  'eudr-forest-carbon-satellite-auditor',
  'water-basin-eutrophication-sat-analyst',
  'waste-landfill-methane-sat-inspector',
  'natura2000-emerald-biodiversity-monitor',
  'soil-degradation-land-erosion-analyst',
  'war-ecocide-environmental-damage-evaluator',
  'rad-nuclear-eco-satellite-monitor',
  'tailings-dam-geohazard-gnss-auditor',
  'wildfire-peatland-thermal-gnss-tracker',
  'landslide-slope-stability-gnss-analyst',
  'industrial-air-emission-skako-auditor',
  'coastal-marine-oilspill-sat-inspector',
  'hydrological-drought-groundwater-gnss-modeler',
  'illegal-quarry-amber-mining-skako-sentinel'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Leaf className={className} />;
  return <IconComponent className={className} />;
};

export default function EcoCenterHubView({
  onLaunchAgent,
  onBack,
  language,
  currency = 'UAH'
}: EcoCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const [selectedSubTab, setSelectedSubTab] = useState<'all' | 'analyzer' | 'strategy' | 'forest_eudr' | 'water_marine' | 'waste_soil' | 'nuclear_air' | 'geohazards_dam' | 'war_mining'>('all');
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch eco pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const allEcoAgents = agentsConfig.filter((a: any) => ECO_AGENT_IDS.includes(a.id));

  const filteredAgents = allEcoAgents.filter((agent: any) => {
    if (selectedSubTab === 'all') return true;
    if (selectedSubTab === 'strategy') {
      return ['eco-space-strategy-architect'].includes(agent.id);
    }
    if (selectedSubTab === 'forest_eudr') {
      return ['eudr-forest-carbon-satellite-auditor', 'natura2000-emerald-biodiversity-monitor'].includes(agent.id);
    }
    if (selectedSubTab === 'water_marine') {
      return ['water-basin-eutrophication-sat-analyst', 'coastal-marine-oilspill-sat-inspector'].includes(agent.id);
    }
    if (selectedSubTab === 'waste_soil') {
      return ['waste-landfill-methane-sat-inspector', 'soil-degradation-land-erosion-analyst'].includes(agent.id);
    }
    if (selectedSubTab === 'nuclear_air') {
      return ['rad-nuclear-eco-satellite-monitor', 'industrial-air-emission-skako-auditor'].includes(agent.id);
    }
    if (selectedSubTab === 'geohazards_dam') {
      return ['tailings-dam-geohazard-gnss-auditor', 'landslide-slope-stability-gnss-analyst', 'hydrological-drought-groundwater-gnss-modeler', 'wildfire-peatland-thermal-gnss-tracker'].includes(agent.id);
    }
    if (selectedSubTab === 'war_mining') {
      return ['war-ecocide-environmental-damage-evaluator', 'illegal-quarry-amber-mining-skako-sentinel'].includes(agent.id);
    }
    return true;
  });

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono max-w-7xl mx-auto">
      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
            {isPl ? '🌿 15 Modułów Ekologii & Green Deal' : isUk ? '🌿 15 Експертних Модулів Довкілля' : '🌿 15 Eco & Green Deal Modules'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-teal-500/20 text-teal-300 border border-teal-400/30 rounded-md flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-teal-400" />
            {isPl ? 'ДЗЗ • СКАКО / ГЦСК • ГНСС • EUDR • RD4U' : isUk ? 'ДЗЗ • СКАКО / ГЦСК • ГНСС • EUDR • RD4U' : 'EO • SKAKO / GCSS • GNSS • EUDR'}
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
              {isPl ? 'ŚRODOWISKO • ZIELONY ŁAD • DZZ • SKAKO • GNSS • MONITORING BEZPIECZEŃSTWA' : isUk ? 'ДОВКІЛЛЯ • GREEN DEAL • ДЗЗ • СКАКО • ГНСС • ТЕХНОГЕННА БЕЗПЕКА' : 'ENVIRONMENT • GREEN DEAL • EO • SKAKO • GNSS • GEOHAZARD SAFETY'}
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight uppercase leading-tight bg-gradient-to-r from-white via-emerald-100 to-teal-300 bg-clip-text text-transparent">
            {isPl 
              ? 'CENTRUM EKOLOGII, KLIMATU & GREEN DEAL' 
              : isUk 
              ? 'ЕКОЛОГІЯ, КЛІМАТ & ЗЕЛЕНИЙ ПЕРЕХІД' 
              : 'ECO, CLIMATE & GREEN DEAL HUB'}
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            {isPl
              ? 'Narodowy system monitoringu środowiska łączący teledetekcję satelitarną (Copernicus, Sentinel-2/5P, InSAR), geofizyczny system kontroli specjalnej SKAKO (promieniowanie, infradźwięk, sejsmoakustyka) oraz precyzyjne pomiary GNSS. Weryfikacja EUDR 2023/1115, stabilność zapór, wycieki metanu i rejestr strat wojennych RD4U.'
              : isUk
              ? 'Національний комплекс моніторингу довкілля та техногенної безпеки на базі супутникових даних ДЗЗ (Sentinel-1/2/5P, InSAR, Landsat, GRACE), радіаційно-сейсмоакустичного спецконтролю СКАКО/ГЦСК та високоточної геодезії ГНСС. Комплаєнс EUDR 2023/1115, контроль хвостосховищ, радіаційний фон АЕС, якість вод та фіксація екоциду для Гааги (RD4U).'
              : 'National environmental intelligence & geohazard safety platform fusing Earth Observation (Sentinel-1/2/5P, InSAR, GRACE), SKAKO geophysical special control (radiation, infrasound, seismoacoustics), and high-precision GNSS geodesy.'}
          </p>
        </div>
      </div>

      {/* Sub-Category Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
        <button
          onClick={() => setSelectedSubTab('all')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'all'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? 'Wszystkie Moduły (15)' : isUk ? 'Всі Модулі Довкілля (15)' : 'All Eco Modules (15)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('analyzer')}
          className={`px-4 py-2 rounded-xl text-xs font-extrabold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'analyzer'
              ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-lg shadow-teal-500/30 border border-teal-300'
              : 'bg-teal-950/60 text-teal-300 hover:bg-teal-900 border border-teal-500/40'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5 text-teal-300 animate-pulse" />
          <span>{isPl ? '🛰️ Analizator Gleb & UKF' : isUk ? '🛰️ Аналізатор Ґрунтів & UKF' : '🛰️ Soil & UKF Analyzer'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('strategy')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'strategy'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '🏛️ Strategia & Granty (1)' : isUk ? '🏛️ Стратегія & Гранти (1)' : '🏛️ Strategy & Grants (1)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('forest_eudr')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'forest_eudr'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '🌲 Lasy, EUDR & Natura 2000 (2)' : isUk ? '🌲 Ліси, EUDR & Смарагдова Мережа (2)' : '🌲 Forests & EUDR (2)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('water_marine')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'water_marine'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '💧 Rzeki & Morza (2)' : isUk ? '💧 Річки & Моря (2)' : '💧 Rivers & Marine (2)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('nuclear_air')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'nuclear_air'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '☢️ Radiacja & Smog (2)' : isUk ? '☢️ Радіація АЕС & Смог (2)' : '☢️ Nuclear & Air Smog (2)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('geohazards_dam')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'geohazards_dam'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '⛰️ Tamy, Osuwiska & Pożary GNSS (4)' : isUk ? '⛰️ Дамби, Зсуви & Торфовища ГНСС (4)' : '⛰️ Dams & Geohazards (4)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('waste_soil')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'waste_soil'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '♻️ Odpady & Gleby (2)' : isUk ? '♻️ Відходи, Метан & Ґрунти (2)' : '♻️ Waste & Soil (2)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('war_mining')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'war_mining'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '⚔️ Ekobójstwo & Kopaliny (2)' : isUk ? '⚔️ Екоцид & Надра (2)' : '⚔️ War Ecocide & Mining (2)'}
        </button>
      </div>

      {/* Conditional Rendering: Interactive Analyzer View */}
      {selectedSubTab === 'analyzer' ? (
        <EcoCopernicusSoilUkfAnalyzer
          language={language}
          onBack={() => setSelectedSubTab('all')}
          currency={currency}
        />
      ) : (
        <>
          {/* Featured Showcase Card for Analyzer on 'all' tab */}
          {selectedSubTab === 'all' && (
            <div className="bg-gradient-to-r from-slate-950 via-teal-950/70 to-emerald-950/80 border-2 border-teal-500/50 hover:border-teal-400 rounded-2xl p-6 shadow-2xl transition-all group flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="space-y-3 max-w-2xl">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2.5 py-0.5 text-[9px] font-black uppercase bg-teal-500/30 text-teal-300 border border-teal-400/40 rounded flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-teal-300 animate-spin" />
                    {isPl ? 'NOWOŚĆ • INTERAKTYWNY MODUŁ BADAWCZY' : isUk ? 'НОВИНКА • ІНТЕРАКТИВНИЙ ДОСЛІДНИЦЬКИЙ МОДУЛЬ' : 'NEW • INTERACTIVE RESEARCH MODULE'}
                  </span>
                  <span className="text-[9px] font-bold uppercase bg-slate-900 border border-slate-700 text-slate-300 px-2 py-0.5 rounded">
                    Банах А.С. (100% IP) | Спец. 183 PhD
                  </span>
                </div>
                <h3 className="text-xl font-extrabold uppercase text-white group-hover:text-teal-300 transition-colors">
                  🛰️ ECO-COPERNICUS Soil & UKF Analyzer
                </h3>
                <p className="text-xs text-slate-300 font-mono leading-relaxed">
                  {isUk
                    ? 'Всепогодний супутниковий моніторинг деструкту ґрунтів (Sentinel-1 InSAR CCD Δγ ≥ 0.35, Sentinel-2 BSI), нелінійна фільтрація Калмана (5D UKF на 11 сигма-точках), балістичний калькулятор вирв Садовського та розрахунок збитків за Наказом Міндовкілля № 167.'
                    : isPl
                    ? 'Satelitarny monitoring degradacji gleb (Sentinel-1 InSAR CCD, Sentinel-2 BSI), nieliniowy filtr Kalmana (5D UKF na 11 punktach sigma), kalkulator leja Sadowskiego i szacowanie strat wg Rozporządzenia nr 167.'
                    : 'All-weather satellite soil degradation auditing (Sentinel-1 InSAR CCD, Sentinel-2 BSI), 5D Unscented Kalman Filtering (11 sigma points), Sadovsky crater inversion, and official environmental damages under Order No. 167.'}
                </p>
                <div className="flex items-center gap-3 text-[10px] text-teal-300 font-mono flex-wrap">
                  <span>✓ 4 еталонні полігони України</span>
                  <span>✓ Невизначеність GUM ±3.02 мм</span>
                  <span>✓ Селектор фіторемедіації</span>
                  <span>✓ Експорт Акту (DOCX/MD)</span>
                </div>
              </div>

              <button
                onClick={() => setSelectedSubTab('analyzer')}
                className="w-full md:w-auto px-6 py-3.5 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-400 hover:to-emerald-500 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-xl shadow-teal-500/30 flex items-center justify-center gap-2 cursor-pointer shrink-0 group-hover:scale-105"
              >
                <span>{isPl ? '🚀 Uruchom Analizator' : isUk ? '🚀 Відкрити Аналізатор' : '🚀 Launch Analyzer'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Grid of Agent Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredAgents.map((agent: any) => {
              const agentPricing = pricing[agent.id];
              const priceVal = agentPricing ? agentPricing.price : (agent.id === 'eco-space-strategy-architect' ? 2100 : 500);
              const limitVal = agentPricing ? agentPricing.limit : 30;
              const durationDaysVal = agentPricing ? agentPricing.durationDays : 30;
              const singlePriceVal = agentPricing ? agentPricing.singlePrice : (agent.id === 'eco-space-strategy-architect' ? 105 : 25);

              return (
                <div
                  key={agent.id}
                  className="bg-slate-950 border border-emerald-500/30 hover:border-emerald-400 rounded-2xl p-6 flex flex-col justify-between gap-6 transition-all hover:scale-[1.01] shadow-xl group"
                >
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[8px] font-bold uppercase border px-2 py-0.5 rounded bg-emerald-950/60 border-emerald-500/40 text-emerald-300">
                        {getLocalizedAgentCategory(agent, language)}
                      </span>
                      <span className="text-[8px] font-bold uppercase border px-2 py-0.5 rounded bg-purple-950/60 border-purple-500/40 text-purple-300 font-extrabold flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5 text-purple-400" />
                        ✨ Gemini 3.7 Flash
                      </span>
                      <span className="text-[8px] font-bold uppercase border px-2 py-0.5 rounded bg-emerald-950/80 border-emerald-400/50 text-emerald-300 font-bold">
                        {formatPriceWithCurrency(priceVal, currency)} / {limitVal} {isUk ? (limitVal === 1 ? 'запит' : 'запитів') : isPl ? 'zapytań' : 'req.'} ({durationDaysVal} {isPl ? 'dni' : isUk ? 'дн.' : 'days'})
                      </span>
                      {singlePriceVal > 0 && (
                        <span className="text-[8px] font-bold uppercase border px-2 py-0.5 rounded bg-teal-950/80 border-teal-400/50 text-teal-300 font-semibold">
                          {isPl ? `Pojedynczo: ${formatPriceWithCurrency(singlePriceVal, currency)}` : isUk ? `Разово: ${formatPriceWithCurrency(singlePriceVal, currency)}` : `Single: ${formatPriceWithCurrency(singlePriceVal, currency)}`}
                        </span>
                      )}
                      <span className="text-[8px] font-bold uppercase border px-2 py-0.5 rounded bg-green-500/20 border-green-400/40 text-green-300">
                        {isPl ? '🎁 1. uruchomienie BEZPŁATNIE' : isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : '🎁 1st run FREE'}
                      </span>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-emerald-950/80 border border-emerald-500/40 rounded-xl text-emerald-400 group-hover:scale-110 transition-transform">
                        <AgentIcon name={agent.icon} className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-base font-extrabold text-white uppercase tracking-wide group-hover:text-emerald-300 transition-colors">
                          {getLocalizedAgentName(agent, language)}
                        </h2>
                        <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                          {getLocalizedAgentDesc(agent, language)}
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-slate-800/80 pt-4 space-y-2">
                      <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        {isPl ? 'Kluczowe rezultaty & korzyści:' : isUk ? 'Ключові результати та вигода:' : 'Key Outcomes & Value:'}
                      </span>
                      <ul className="space-y-1.5">
                        {getLocalizedAgentFeatures(agent, language).map((feat: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-slate-300">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-800/80">
                    <button
                      onClick={() => onLaunchAgent(agent.target)}
                      className="w-full px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 cursor-pointer group-hover:shadow-emerald-500/50"
                    >
                      <span>{isPl ? '🚀 Otwórz Moduł Ekologiczny' : isUk ? '🚀 Відкрити Модуль Довкілля' : '🚀 Launch Environmental Module'}</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
